// Estimated-balance footer shown at the bottom of the filtered pay-period view.
import { CalendarClock } from "lucide-react";
import { formatMoney } from "@/lib/api";
import { displayFor } from "@/lib/bills-util";

export function PayPeriodFooter({ period, bills, onReschedule }) {
  const income = Number(period.expected_income_amount || 0);
  const billsTotal = bills.reduce((s, b) => {
    const d = displayFor(b, new Date());
    if (d.col === "paid" && !d.notCleared) return s;
    return s + Math.max(0, Number(b.estimated_due_amount) - Number(b.paid_amount || 0));
  }, 0);
  const pendingTotal = Number(period.assigned_pending_total || 0);
  const leftover = income - billsTotal - pendingTotal;
  const positive = leftover >= 0;
  const smallestUnpaid = [...bills]
    .filter((b) => b.status === "unpaid" && Number(b.estimated_due_amount) > 0)
    .sort((a, b) => Number(a.estimated_due_amount) - Number(b.estimated_due_amount))[0];
  return (
    <div
      className={`sticky bottom-0 -mx-1 rounded-2xl border-2 px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 ${positive ? "bg-[#DDE9D8] border-[#8FBF88]" : "bg-[#F5D9D1] border-[#E5B4A3]"}`}
      data-testid="bills-pp-footer"
    >
      <div className={`text-xs font-semibold uppercase tracking-widest ${positive ? "text-[#1F6B2E]" : "text-[#7A2E24]"}`}>
        Estimated balance after these bills are paid
      </div>
      <div className="flex-1 flex items-center gap-4 flex-wrap text-sm">
        <span className={positive ? "text-[#1F6B2E]" : "text-[#7A2E24]"}>
          Scheduled income <span className="font-semibold">+{formatMoney(income)}</span>
        </span>
        <span className={positive ? "text-[#1F6B2E]" : "text-[#7A2E24]"}>
          Bills in view <span className="font-semibold">−{formatMoney(billsTotal)}</span>
        </span>
        {pendingTotal > 0 && (
          <span className={positive ? "text-[#1F6B2E]" : "text-[#7A2E24]"}>
            Pending <span className="font-semibold">−{formatMoney(pendingTotal)}</span>
          </span>
        )}
      </div>
      <div className={`font-heading text-2xl font-semibold ${positive ? "text-[#1F6B2E]" : "text-[#7A2E24]"}`} data-testid="bills-pp-footer-balance">
        {formatMoney(leftover)}
      </div>
      {!positive && smallestUnpaid && (
        <button
          className="btn-ghost !px-3 !py-1.5 text-xs text-[#7A2E24] hover:bg-white/40 whitespace-nowrap"
          onClick={() => onReschedule && onReschedule(smallestUnpaid)}
          data-testid="bills-pp-footer-reschedule"
        >
          <CalendarClock size={12} /> Reschedule smallest ({smallestUnpaid.name})
        </button>
      )}
    </div>
  );
}
